import { Button, Group, Stack, Text } from '@mantine/core'
import {
  Dropzone,
  DropzoneProps,
  FileRejection,
  FileWithPath,
  IMAGE_MIME_TYPE,
  PDF_MIME_TYPE,
} from '@mantine/dropzone'
import { IconPhoto, IconUpload, IconX } from '@tabler/icons-react'
import { useRef, useState } from 'react'

import styles from './dropzone-file-uploader.module.scss'

const DEFAULT_MAX_SIZE = 5 * 1024 ** 2 // 5 MB
const DEFAULT_DROPZONE_TITLE = 'Select a file or drag and drop here'
const DEFAULT_DROPZONE_DESCRIPTION =
  'Upload up to 5 JPG, PNG, or PDF files. File size must not exceed 100MB'

type DropzoneComponentProps = {
  onDrop: (files: FileWithPath[]) => void
  onReject: (files: FileWithPath[]) => void
  dropZoneTitle?: string
  dropZoneDescription?: string
  isLoading: boolean
} & Partial<DropzoneProps>

const DropZoneFileUploaderComponent = ({
  onDrop,
  onReject,
  accept = [...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE],
  maxSize = DEFAULT_MAX_SIZE,
  dropZoneTitle = DEFAULT_DROPZONE_TITLE,
  dropZoneDescription = DEFAULT_DROPZONE_DESCRIPTION,
  isLoading,
  ...props
}: DropzoneComponentProps) => {
  const [rejectMessage, setRejectMessage] = useState<string | null>(null)

  const openRef = useRef<() => void>(null)
  const [dropZoneKey, setDropZoneKey] = useState(1)
  const handleReject = (files: FileRejection[]) => {
    onReject(files)
    setRejectMessage(files[0]?.errors[0].message || '')
  }

  const onDropFile = (files: FileWithPath[]) => {
    onDrop(files)
  }

  return (
    <Dropzone
      onDrop={onDropFile}
      onReject={handleReject}
      maxSize={maxSize}
      accept={accept}
      activateOnClick={false}
      openRef={openRef}
      key={dropZoneKey}
      loading={isLoading}
      className={styles.dropzone}
      {...props}
    >
      <Dropzone.Accept>
        <Group
          justify="between"
          gap="xl"
          mih={80}
          wrap="nowrap"
          style={{ pointerEvents: 'none' }}
        >
          <IconUpload
            size={48}
            color="var(--mantine-color-blue-6)"
            stroke={1.5}
          />
          <Stack gap={10}>
            <Text size="md" inline>
              Uploading file...
            </Text>
          </Stack>
        </Group>
      </Dropzone.Accept>
      <Dropzone.Reject>
        <Group justify="between" gap="xl" mih={80} wrap="nowrap">
          <IconX
            size={48}
            color="var(--mantine-color-red-6)"
            stroke={1.5}
            onClick={() => setDropZoneKey((prev) => ++prev)}
            style={{ pointerEvents: 'all' }}
          />
          <Stack gap={10}>
            <Text size="md" inline>
              File rejected
            </Text>
            <Text size="sm" c="dimmed" inline mt={7}>
              {rejectMessage}
            </Text>
          </Stack>
        </Group>
      </Dropzone.Reject>
      <Dropzone.Idle>
        <Group
          justify="center"
          gap="xl"
          mih={80}
          style={{ pointerEvents: 'none' }}
          wrap="nowrap"
        >
          <IconPhoto
            size={48}
            color="var(--mantine-color-dimmed)"
            stroke={1.5}
          />
          <Stack gap={10}>
            <Text size="md" inline>
              {dropZoneTitle}
            </Text>
            <Text size="sm" c="dimmed" inline mt={7}>
              {dropZoneDescription}
            </Text>
          </Stack>
          <Button
            variant="default"
            onClick={() => openRef.current?.()}
            style={{ pointerEvents: 'all' }}
            classNames={{
              root: styles['button-cancel'],
              label: styles['label'],
            }}
          >
            Select files
          </Button>
        </Group>
      </Dropzone.Idle>
    </Dropzone>
  )
}

export default DropZoneFileUploaderComponent
